import { useEffect, useRef } from 'react';

import type { AgentForm } from '../../../utilities';
import type { AgentResponse, AiModelData } from '../../../api';
import { resolveAgentModelSettings } from '../utils/resolveAgentModelSettings';

type UseSyncedModelSettingsProps = {
  form: AgentForm | null;
  data: AgentResponse | null;
  setForm: React.Dispatch<React.SetStateAction<AgentForm | null>>;
  setFormId: React.Dispatch<React.SetStateAction<number>>;
  models: {
    ttsPrimary: AiModelData;
    ttsBackup: AiModelData;
    sttPrimary: AiModelData;
    sttBackup: AiModelData;
    llmPrimary: AiModelData;
    llmBackup: AiModelData;
    llmTertiary: AiModelData;
  };
};

type PrevModelIds = {
  tts_primary_model_id?: string;
  tts_backup_model_id?: string;
  stt_primary_model_id?: string;
  stt_backup_model_id?: string;
  llm_primary_model_id?: string;
  llm_backup_model_id?: string;
  llm_tertiary_model_id?: string;
};

export const useSyncedModelSettings = ({
  form,
  data,
  setForm,
  setFormId,
  models,
}: UseSyncedModelSettingsProps) => {
  const {
    ttsPrimary,
    ttsBackup,
    sttPrimary,
    sttBackup,
    llmPrimary,
    llmBackup,
    llmTertiary,
  } = models;

  const prevModelIds = useRef<PrevModelIds>({});

  useEffect(() => {
    if (
      ttsPrimary.modelName &&
      ttsBackup.modelName &&
      sttPrimary.modelName &&
      sttBackup.modelName &&
      llmPrimary.modelName &&
      llmBackup.modelName
    ) {
      const prev = prevModelIds.current;

      setForm((f: any) => {
        if (!f) return f;

        return {
          ...f,
          tts_primary_model_settings: resolveAgentModelSettings(
            f.tts_primary_model_id,
            prev.tts_primary_model_id,
            data?.tts_primary_model_id,
            data?.tts_primary_model_settings,
            f.tts_primary_model_settings,
            ttsPrimary.settings
          ),
          tts_backup_model_settings: resolveAgentModelSettings(
            f.tts_backup_model_id,
            prev.tts_backup_model_id,
            data?.tts_backup_model_id,
            data?.tts_backup_model_settings,
            f.tts_backup_model_settings,
            ttsBackup.settings
          ),
          stt_primary_model_settings: resolveAgentModelSettings(
            f.stt_primary_model_id,
            prev.stt_primary_model_id,
            data?.stt_primary_model_id,
            data?.stt_primary_model_settings,
            f.stt_primary_model_settings,
            sttPrimary.settings
          ),
          stt_backup_model_settings: resolveAgentModelSettings(
            f.stt_backup_model_id,
            prev.stt_backup_model_id,
            data?.stt_backup_model_id,
            data?.stt_backup_model_settings,
            f.stt_backup_model_settings,
            sttBackup.settings
          ),
          llm_primary_model_settings: resolveAgentModelSettings(
            f.llm_primary_model_id,
            prev.llm_primary_model_id,
            data?.llm_primary_model_id,
            data?.llm_primary_model_settings,
            f.llm_primary_model_settings,
            llmPrimary.settings
          ),
          llm_backup_model_settings: resolveAgentModelSettings(
            f.llm_backup_model_id,
            prev.llm_backup_model_id,
            data?.llm_backup_model_id,
            data?.llm_backup_model_settings,
            f.llm_backup_model_settings,
            llmBackup.settings
          ),
          llm_tertiary_model_settings: resolveAgentModelSettings(
            f.llm_tertiary_model_id,
            prev.llm_tertiary_model_id,
            data?.llm_tertiary_model_id,
            data?.llm_tertiary_model_settings,
            f.llm_tertiary_model_settings,
            llmTertiary.settings
          ),
        };
      });

      prevModelIds.current = {
        tts_primary_model_id: form?.tts_primary_model_id,
        tts_backup_model_id: form?.tts_backup_model_id,
        stt_primary_model_id: form?.stt_primary_model_id,
        stt_backup_model_id: form?.stt_backup_model_id,
        llm_primary_model_id: form?.llm_primary_model_id,
        llm_backup_model_id: form?.llm_backup_model_id,
        llm_tertiary_model_id: form?.llm_tertiary_model_id ?? undefined,
      };

      setFormId(f => f + 1);
    }
  }, [
    ttsPrimary.modelName,
    ttsBackup.modelName,
    data?.tts_primary_model_settings,
    data?.tts_backup_model_settings,
    form?.tts_primary_model_id,
    form?.tts_backup_model_id,
    setForm,
    setFormId,
    data?.tts_primary_model_id,
    data?.tts_backup_model_id,
    sttPrimary.modelName,
    data?.stt_primary_model_settings,
    data?.stt_primary_model_id,
    form?.stt_primary_model_id,
    sttBackup.modelName,
    sttBackup.settings,
    data?.stt_backup_model_settings,
    data?.stt_backup_model_id,
    form?.stt_backup_model_id,
    ttsPrimary.settings,
    ttsBackup.settings,
    sttPrimary.settings,
    llmPrimary.modelName,
    llmPrimary.settings,
    data?.llm_primary_model_settings,
    data?.llm_primary_model_id,
    form?.llm_primary_model_id,
    llmBackup.modelName,
    llmBackup.settings,
    data?.llm_backup_model_settings,
    data?.llm_backup_model_id,
    form?.llm_backup_model_id,
    llmTertiary.modelName,
    llmTertiary.settings,
    data?.llm_tertiary_model_settings,
    data?.llm_tertiary_model_id,
    form?.llm_tertiary_model_id,
  ]);
};
